function escapeCell(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Trigger a CSV file download in the browser. */
export function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map(escapeCell).join(",")).join("\r\n");
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadPartyLedgerCsv(detail) {
  if (!detail?.party) return;
  const p = detail.party;
  const today = new Date().toISOString().slice(0, 10);
  const rows = [
    ["Financial Ledger"],
    ["Party", p.name],
    ["Code", p.code],
    ["Generated", today],
    ["Closing Balance", Number(detail.closing_balance || 0).toFixed(2)],
    [],
    ["Date", "Type", "Description", "Reference", "Debit", "Credit", "Balance"],
    ...(detail.ledger || []).map((e) => [
      e.date ? String(e.date).slice(0, 10) : "",
      e.entry_type || "",
      e.description || "",
      e.source_txn_number || "",
      Number(e.debit || 0).toFixed(2),
      Number(e.credit || 0).toFixed(2),
      Number(e.balance || 0).toFixed(2),
    ]),
  ];
  const safeCode = (p.code || "party").replace(/[^\w.-]+/g, "_");
  downloadCsv(`${safeCode}-ledger-${today}.csv`, rows);
}

function partyStockMeta(detail) {
  const p = detail?.party || {};
  const today = new Date().toISOString().slice(0, 10);
  const safeCode = (p.code || "party").replace(/[^\w.-]+/g, "_");
  return { p, today, safeCode, stock: detail?.stock || [] };
}

/** Party current stock — one row per lot. */
export function downloadPartyStockLotCsv(detail) {
  if (!detail?.party) return;
  const { p, today, safeCode, stock } = partyStockMeta(detail);
  const totalBags = stock.reduce((a, s) => a + Number(s.bags || 0), 0);
  const totalQtl = stock.reduce((a, s) => a + Number(s.weight || 0), 0);
  const rows = [
    ["Current Stock — Lot wise"],
    ["Party", p.name],
    ["Code", p.code],
    ["Generated", today],
    ["Lots", stock.length],
    ["Total Bags", totalBags],
    ["Total Weight (Qtl)", Number(totalQtl).toFixed(3)],
    [],
    ["Lot Number", "Item", "Warehouse", "Stack", "Bags", "Weight (Qtl)"],
    ...stock.map((s) => [
      s.lot_number || "",
      s.item || "",
      s.warehouse || "",
      s.stack || "",
      Number(s.bags || 0),
      Number(s.weight || 0).toFixed(3),
    ]),
  ];
  downloadCsv(`${safeCode}-stock-lotwise-${today}.csv`, rows);
}

/** Party current stock — aggregated by warehouse + stack. */
export function downloadPartyStockStackCsv(detail) {
  if (!detail?.party) return;
  const { p, today, safeCode, stock } = partyStockMeta(detail);
  const byStack = {};
  for (const s of stock) {
    const wh = s.warehouse || "";
    const st = s.stack || "";
    const key = `${wh}\t${st}`;
    if (!byStack[key]) {
      byStack[key] = { warehouse: wh, stack: st, bags: 0, weight: 0, lots: 0, items: new Set() };
    }
    byStack[key].bags += Number(s.bags || 0);
    byStack[key].weight += Number(s.weight || 0);
    byStack[key].lots += 1;
    if (s.item) byStack[key].items.add(s.item);
  }
  const stacks = Object.values(byStack).sort((a, b) =>
    `${a.warehouse}/${a.stack}`.localeCompare(`${b.warehouse}/${b.stack}`)
  );
  const totalBags = stacks.reduce((a, s) => a + s.bags, 0);
  const totalQtl = stacks.reduce((a, s) => a + s.weight, 0);
  const rows = [
    ["Current Stock — Stack wise"],
    ["Party", p.name],
    ["Code", p.code],
    ["Generated", today],
    ["Stacks", stacks.length],
    ["Total Bags", totalBags],
    ["Total Weight (Qtl)", Number(totalQtl).toFixed(3)],
    [],
    ["Warehouse", "Stack", "Lots", "Items", "Bags", "Weight (Qtl)"],
    ...stacks.map((s) => [
      s.warehouse,
      s.stack,
      s.lots,
      [...s.items].join("; "),
      s.bags,
      Number(s.weight).toFixed(3),
    ]),
  ];
  downloadCsv(`${safeCode}-stock-stackwise-${today}.csv`, rows);
}

/** Rent bill line detail (single bill print/export). */
export function downloadRentBillCsv(data) {
  if (!data?.bill) return;
  const b = data.bill;
  const today = new Date().toISOString().slice(0, 10);
  const rows = [
    ["Storage Rent Bill"],
    ["Bill Number", b.bill_number || ""],
    ["Status", data.status || b.status || ""],
    ["Party", data.party_name || ""],
    ["Party Code", data.party_code || ""],
    ["Warehouse", data.warehouse_name || ""],
    ["Contract", data.contract_number || ""],
    ["Method", data.method || ""],
    ["Period From", data.period_from || ""],
    ["Period To", data.period_to || ""],
    ["Tax Treatment", data.tax_treatment || b.tax_treatment || ""],
    ["Formula", data.basis_label || ""],
    ["Generated", today],
    [],
    ["Period", "Lot", "Stack", "Bag Type", "Bags/Day", "Days", "Rent/Day", "Calculation", "Amount"],
    ...(data.statement || []).map((r) => [
      r.from === r.to ? r.from : `${r.from} to ${r.to}`,
      r.lot_number || "",
      r.stack_code || "",
      r.bag_type_name || "",
      Number(r.bags_per_day ?? r.bags ?? 0),
      Number(r.days || 0),
      Number(r.daily_rent || 0).toFixed(2),
      r.calculation || "",
      Number(r.amount || 0).toFixed(2),
    ]),
    [],
    ["Total", "", "", "", "", "", "", "", Number(data.amount || b.amount || 0).toFixed(2)],
  ];
  const safeBill = (b.bill_number || "rent-bill").replace(/[^\w.-]+/g, "_");
  downloadCsv(`${safeBill}.csv`, rows);
}

/** Rent statement (ad-hoc period, no bill record). */
export function downloadRentStatementCsv(data) {
  if (!data) return;
  const today = new Date().toISOString().slice(0, 10);
  const rows = [
    ["Storage Rent Statement"],
    ["Party", data.party_name || ""],
    ["Party Code", data.party_code || ""],
    ["Warehouse", data.warehouse_name || ""],
    ["Contract", data.contract_number || ""],
    ["Method", data.method || ""],
    ["Period From", data.period_from || ""],
    ["Period To", data.period_to || ""],
    ["Formula", data.basis_label || ""],
    ["Generated", today],
    [],
    ["Period", "Lot", "Stack", "Bag Type", "Bags/Day", "Days", "Rent/Day", "Calculation", "Amount"],
    ...(data.statement || []).map((r) => [
      r.from === r.to ? r.from : `${r.from} to ${r.to}`,
      r.lot_number || "",
      r.stack_code || "",
      r.bag_type_name || "",
      Number(r.bags_per_day ?? r.bags ?? 0),
      Number(r.days || 0),
      Number(r.daily_rent || 0).toFixed(2),
      r.calculation || "",
      Number(r.amount || 0).toFixed(2),
    ]),
    [],
    ["Total", "", "", "", "", "", "", "", Number(data.amount || 0).toFixed(2)],
  ];
  const safeCode = (data.party_code || "rent").replace(/[^\w.-]+/g, "_");
  downloadCsv(`${safeCode}-rent-${data.period_from || today}.csv`, rows);
}

/** Summary export of all rent bills in the list. */
export function downloadRentBillsListCsv(bills) {
  const today = new Date().toISOString().slice(0, 10);
  const rows = [
    ["Rent Bills Register"],
    ["Generated", today],
    [],
    ["Bill Number", "Party", "Contract", "Warehouse", "Period From", "Period To", "Method", "Basis", "Amount", "Status", "Posting Type"],
    ...(bills || []).map((b) => [
      b.bill_number || "",
      b.party_name || "",
      b.contract_number || "",
      b.warehouse_name || "",
      b.period_from || "",
      b.period_to || "",
      b.method || "",
      b.basis_label || "",
      Number(b.amount || 0).toFixed(2),
      b.status || "",
      b.posting_type || "",
    ]),
  ];
  downloadCsv(`rent-bills-${today}.csv`, rows);
}
